/**
 * Cart drawer behaviour and product-form progressive enhancement.
 * Cart requests go through window.ThemeCart only.
 */
(function () {
  'use strict';

  if (document.documentElement.hasAttribute('data-cart-drawer-bound')) return;
  document.documentElement.setAttribute('data-cart-drawer-bound', '');

  var lastFocus = null;
  var pendingTrigger = null;
  var lockedScroll = 0;
  var bodyLocked = false;

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function isOpen() {
    var root = drawer();
    return !!(root && root.classList.contains('is-open'));
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setExpanded(open) {
    var triggers = document.querySelectorAll('.cart-drawer-trigger');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  function lockScroll() {
    if (bodyLocked) return;
    lockedScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + lockedScroll + 'px';
    document.body.style.width = '100%';
    bodyLocked = true;
  }

  function unlockScroll() {
    if (!bodyLocked) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    bodyLocked = false;
    window.scrollTo(0, lockedScroll);
  }

  function focusable(container) {
    if (!container) return [];
    var nodes = container.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var list = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest('[hidden]')) continue;
      if (el.getAttribute('aria-hidden') === 'true') continue;
      if (el.tabIndex < 0) continue;
      if (!el.getClientRects().length) continue;
      list.push(el);
    }
    return list;
  }

  function showDrawer() {
    var root = drawer();
    if (!root || root.classList.contains('is-open')) return;
    var panel = root.querySelector('.cart-drawer__panel');
    var overlay = root.querySelector('[data-cart-drawer-overlay]');
    if (!panel) return;

    lastFocus = pendingTrigger && typeof pendingTrigger.focus === 'function' ? pendingTrigger : document.activeElement;
    pendingTrigger = null;
    if (overlay) overlay.hidden = false;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    lockScroll();
    setExpanded(true);

    var reveal = function () {
      root.classList.add('is-open');
      var items = focusable(panel);
      if (items.length) items[0].focus();
      else panel.focus();
      syncRecoNav();
    };

    if (reducedMotion()) {
      reveal();
    } else {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(reveal);
      });
    }
    syncWalletGuard();
  }

  function hideDrawer() {
    var root = drawer();
    if (!root || !root.classList.contains('is-open')) return;
    var panel = root.querySelector('.cart-drawer__panel');
    var overlay = root.querySelector('[data-cart-drawer-overlay]');
    root.classList.remove('is-open');
    setExpanded(false);

    var finish = function () {
      if (panel) {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      }
      if (overlay) overlay.hidden = true;
      unlockScroll();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    };

    if (!panel || reducedMotion()) {
      finish();
      return;
    }

    var settled = false;
    var done = function (event) {
      if (event && event.target !== panel) return;
      if (settled) return;
      settled = true;
      panel.removeEventListener('transitionend', done);
      finish();
    };
    panel.addEventListener('transitionend', done);
    window.setTimeout(done, 400);
  }

  function requestOpen(trigger) {
    pendingTrigger = trigger || null;
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  function requestClose() {
    document.dispatchEvent(new CustomEvent('cart:close'));
  }

  function normalizePath(value) {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (error) {
      return '';
    }
  }

  function cartPath(root) {
    var expected = (root && root.getAttribute('data-cart-url')) || '/cart';
    return normalizePath(expected) || '/cart';
  }

  function isCartPath(path) {
    if (!path) return false;
    if (path === '/cart') return true;
    if (path === cartPath(drawer())) return true;
    var routes = window.Shopify && window.Shopify.routes;
    var prefix = (routes && routes.root) || '/';
    if (prefix.charAt(prefix.length - 1) !== '/') prefix += '/';
    return path === normalizePath(prefix + 'cart');
  }

  function isCartLink(anchor) {
    if (!drawer() || !anchor || anchor.tagName !== 'A') return false;
    if (anchor.closest('[data-cart-drawer]')) return false;
    if (anchor.hasAttribute('data-cart-drawer-ignore')) return false;
    if (anchor.getAttribute('target') === '_blank') return false;
    if (anchor.hasAttribute('data-cart-link')) return true;
    var href = anchor.getAttribute('href');
    if (!href || href.charAt(0) === '#') return false;
    return isCartPath(normalizePath(href));
  }

  function statusNode() {
    var root = drawer();
    return root ? root.querySelector('[data-cart-drawer-status]') : null;
  }

  function setStatus(message) {
    var status = statusNode();
    if (status) status.textContent = message || '';
  }

  function lineKeySelector(key) {
    var lines = document.querySelectorAll('[data-cart-line]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-line-key') === key) return lines[i];
    }
    return null;
  }

  function updateLine(input, quantity) {
    if (!window.ThemeCart || !input) return;
    var line = input.closest('[data-cart-line]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var lineIndex = line.getAttribute('data-line');
    if (!key && !lineIndex) return;

    setStatus('');
    var slot = line.querySelector('[data-line-error]');
    if (slot) slot.textContent = '';
    input.value = String(quantity);
    line.classList.add('is-loading');
    line.setAttribute('aria-busy', 'true');

    var payload = { quantity: quantity };
    if (key) payload.id = key;
    else payload.line = parseInt(lineIndex, 10);

    window.ThemeCart.change(payload).then(function () {
      setStatus('');
    }).catch(function (error) {
      var message = error && error.message ? error.message : '';
      return window.ThemeCart.refresh().then(function () {
        setStatus(message);
        var fresh = lineKeySelector(key);
        var errorSlot = fresh && fresh.querySelector('[data-line-error]');
        if (errorSlot) errorSlot.textContent = message;
      }).catch(function () {
        setStatus(message);
      });
    });
  }

  function opensAfterAdd() {
    return !!drawer();
  }

  function isAddForm(form) {
    if (!form || !form.getAttribute) return false;
    var action = form.getAttribute('action') || '';
    if (!action) return false;
    try {
      return /\/cart\/add\/?$/.test(new URL(action, window.location.origin).pathname);
    } catch (error) {
      return false;
    }
  }

  // Adds no longer open the drawer; assets/cart-toast.js listens for this and
  // shows a toast instead. The drawer still opens from the cart icon or "View cart".
  function announceAdd(title) {
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { title: title || '' } }));
  }

  function titleFromForm(form) {
    if (!form) return '';
    var explicit = form.getAttribute('data-product-title');
    if (explicit) return explicit;
    var card = form.closest && form.closest('.category-card');
    var node = card && card.querySelector('.indiv-product-title-text');
    if (!node) node = document.querySelector('.product-page h1, .product-title, h1.product-title, h1');
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function itemsFromForm(form) {
    var data = new FormData(form);
    var id = data.get('id');
    if (!id) throw new Error('Missing variant');
    var quantity = parseInt(data.get('quantity') || '1', 10);
    if (!quantity || quantity < 1) quantity = 1;
    var item = { id: id, quantity: quantity };
    var properties = {};
    data.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (match && String(value) !== '') properties[match[1]] = String(value);
    });
    if (Object.keys(properties).length) item.properties = properties;
    var sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = String(sellingPlan);
    return [item];
  }

  function addFromForm(form, button) {
    if (!window.ThemeCart || !form) return;
    if (form.getAttribute('data-cart-pending') === 'true') return;
    if (button && button.disabled) return;
    form.setAttribute('data-cart-pending', 'true');
    if (button) button.setAttribute('aria-busy', 'true');

    var items;
    try {
      items = itemsFromForm(form);
    } catch (error) {
      form.removeAttribute('data-cart-pending');
      if (button) button.removeAttribute('aria-busy');
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    window.ThemeCart.add(items).then(function () {
      form.removeAttribute('data-cart-pending');
      if (button) button.removeAttribute('aria-busy');
      if (button && typeof button.focus === 'function') button.focus();
      announceAdd(titleFromForm(form));
    }).catch(function () {
      form.removeAttribute('data-cart-pending');
      if (button) button.removeAttribute('aria-busy');
      HTMLFormElement.prototype.submit.call(form);
    });
  }

  function productAddButton(target) {
    if (!target || !target.closest) return null;
    var button = target.closest('button, input[type="submit"]');
    if (!button) return null;
    if (button.name !== 'add' && button.id !== 'purchase') return null;
    var form = button.form || button.closest('form');
    if (!isAddForm(form)) return null;
    return { button: button, form: form };
  }

  function termsBlocked(node) {
    var root = node && node.closest ? node.closest('[data-cart-drawer]') : drawer();
    if (!root) return false;
    var agree = root.querySelector('[data-cart-drawer-agree]');
    if (!agree || agree.checked) return false;
    var message = agree.getAttribute('data-terms-agree') || '';
    setStatus(message);
    window.alert(message);
    agree.focus();
    return true;
  }

  function saveNote() {
    var root = drawer();
    if (!root || !window.ThemeCart || typeof window.ThemeCart.update !== 'function') {
      return Promise.resolve();
    }
    var note = root.querySelector('[data-cart-drawer-note]');
    if (!note) return Promise.resolve();
    return window.ThemeCart.update({ note: note.value });
  }

  function checkoutDestination() {
    return cartPath(drawer()).replace(/\/cart$/, '/checkout');
  }

  function beginCheckout(button) {
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    if (termsBlocked(button)) return;
    var form = button.closest('form');
    window.clearTimeout(noteTimer);
    button.setAttribute('aria-busy', 'true');
    saveNote().then(function () {
      var action = (form && form.getAttribute('action')) || '';
      if (action.indexOf('/apps/bundles') !== -1) {
        var finish = function () {
          var fresh = drawer() && drawer().querySelector('.cart-drawer__form');
          HTMLFormElement.prototype.submit.call(fresh || form);
        };
        if (window.ThemeCart && window.ThemeCart.refresh) {
          return window.ThemeCart.refresh().then(finish, finish);
        }
        finish();
        return;
      }
      window.location.assign(checkoutDestination());
    }).catch(function (error) {
      button.removeAttribute('aria-busy');
      var message = (error && error.message) || '';
      setStatus(message);
      if (message) window.alert(message);
    });
  }

  function syncWalletGuard() {
    var root = drawer();
    if (!root) return;
    var wallets = root.querySelectorAll('.additional-checkout-buttons, .shopify-payment-button');
    var agree = root.querySelector('[data-cart-drawer-agree]');
    for (var i = 0; i < wallets.length; i++) {
      var wallet = wallets[i];
      var guard = wallet.querySelector('[data-cart-wallet-guard]');
      var inner = wallet.querySelector('.dynamic-checkout__content');
      if (!inner) {
        for (var c = 0; c < wallet.children.length; c++) {
          if (!wallet.children[c].hasAttribute('data-cart-wallet-guard')) {
            inner = wallet.children[c];
            break;
          }
        }
      }
      if (!agree) {
        if (guard && guard.parentNode) guard.parentNode.removeChild(guard);
        if (inner) inner.inert = false;
        continue;
      }
      if (!guard) {
        guard = document.createElement('button');
        guard.type = 'button';
        guard.className = 'cart-drawer__wallet-guard';
        guard.setAttribute('data-cart-wallet-guard', '');
        wallet.appendChild(guard);
      }
      guard.setAttribute('aria-label', agree.getAttribute('data-terms-agree') || '');
      if (agree.checked) {
        guard.hidden = true;
        if (inner) inner.inert = false;
      } else {
        guard.hidden = false;
        if (inner) inner.inert = true;
      }
    }
  }

  function accordionContent(details) {
    if (!details || !details.children) return null;
    for (var i = 0; i < details.children.length; i++) {
      var child = details.children[i];
      if (child.tagName !== 'SUMMARY') return child;
    }
    return null;
  }

  function syncAccordions(scope) {
    var root = scope || drawer();
    if (!root) return;
    var nodes = root.querySelectorAll('.cart-drawer__note-accordion, .cart-drawer-properties__accordion, .cart-item-bundle');
    for (var i = 0; i < nodes.length; i++) {
      var content = accordionContent(nodes[i]);
      if (!content) continue;
      content.style.overflow = 'hidden';
      content.style.transition = 'none';
      if (nodes[i].open) {
        content.style.maxHeight = 'none';
        content.style.opacity = '1';
      } else {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
      }
    }
  }

  function animateAccordion(details, opening) {
    var content = accordionContent(details);
    if (!content) return;
    if (details.getAttribute('data-accordion-animating') === 'true') return;
    details.setAttribute('data-accordion-animating', 'true');

    var cleaned = false;
    var cleanup = function () {
      if (cleaned) return;
      cleaned = true;
      content.style.willChange = '';
      details.removeAttribute('data-accordion-animating');
    };
    var onDone = function (callback) {
      var handled = false;
      var done = function () {
        if (handled) return;
        handled = true;
        content.removeEventListener('transitionend', onTransitionEnd);
        callback();
        cleanup();
      };
      var onTransitionEnd = function (event) {
        if (!event || event.target !== content) return;
        if (event.propertyName !== 'max-height') return;
        done();
      };
      content.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(done, 380);
    };

    content.style.overflow = 'hidden';
    content.style.willChange = 'max-height, opacity';
    content.style.transition = 'none';

    if (opening) {
      details.open = true;
      content.style.maxHeight = '0px';
      content.style.opacity = '0';
      content.offsetHeight;
      content.style.transition = 'max-height 300ms ease, opacity 220ms ease';
      window.requestAnimationFrame(function () {
        content.style.maxHeight = content.scrollHeight + 'px';
        content.style.opacity = '1';
      });
      onDone(function () {
        content.style.maxHeight = 'none';
      });
      return;
    }

    content.style.transition = 'none';
    content.style.maxHeight = content.scrollHeight + 'px';
    content.style.opacity = '1';
    content.offsetHeight;
    content.style.transition = 'max-height 300ms ease, opacity 220ms ease';
    window.requestAnimationFrame(function () {
      content.style.maxHeight = '0px';
      content.style.opacity = '0';
    });
    onDone(function () {
      details.open = false;
    });
  }

  document.addEventListener('cart:open', showDrawer);
  document.addEventListener('cart:close', hideDrawer);
  document.addEventListener('cart:updated', syncWalletGuard);
  function recoStep(track) {
    var card = track && track.querySelector('.cart-drawer__reco-card');
    if (!card) return 0;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function syncRecoNav() {
    var tracks = document.querySelectorAll('[data-reco-track]');
    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      var section = track.closest('.cart-drawer__recommendations');
      if (!section) continue;
      var max = track.scrollWidth - track.clientWidth;
      var prev = section.querySelector('[data-reco-prev]');
      var next = section.querySelector('[data-reco-next]');
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = max <= 1 || track.scrollLeft >= max - 1;
    }
  }

  function addRecommendation(button) {
    if (!window.ThemeCart || typeof window.ThemeCart.add !== 'function') return;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    var id = button.getAttribute('data-variant-id');
    if (!id) return;
    button.setAttribute('aria-busy', 'true');
    window.ThemeCart.add([{ id: id, quantity: 1 }]).then(function () {
      button.removeAttribute('aria-busy');
      announceAdd(button.getAttribute('data-title') || '');
    }).catch(function (error) {
      button.removeAttribute('aria-busy');
      var message = (error && error.message) || '';
      setStatus(message);
      if (message) window.alert(message);
    });
  }

  document.addEventListener('cart:updated', function () {
    syncAccordions();
    window.requestAnimationFrame(syncRecoNav);
  });

  document.addEventListener('scroll', function (event) {
    var track = event.target;
    if (!track || !track.matches || !track.matches('[data-reco-track]')) return;
    syncRecoNav();
  }, true);

  window.addEventListener('resize', syncRecoNav);
  syncRecoNav();

  // product-bundle dispatches bundle:added after cart/add.js.
  // preventDefault tells it the drawer handled the add, so it does not navigate to the cart page.
  document.addEventListener('bundle:added', function (event) {
    if (!drawer() || !window.ThemeCart || typeof window.ThemeCart.refresh !== 'function') return;
    event.preventDefault();
    window.ThemeCart.refresh().then(function () {
      announceAdd('');
    }, function () {
      announceAdd('');
    });
  });

  var noteTimer = 0;
  document.addEventListener('input', function (event) {
    var note = event.target && event.target.closest && event.target.closest('[data-cart-drawer-note]');
    if (!note || !drawer() || !drawer().contains(note)) return;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(saveNote, 400);
  });

  document.addEventListener('pointerdown', function (event) {
    var root = drawer();
    if (!root || !event.target || !event.target.closest) return;
    if (event.target.closest('[data-cart-wallet-guard]')) return;
    var wallet = event.target.closest('.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout');
    if (!wallet || !root.contains(wallet)) return;
    var agree = root.querySelector('[data-cart-drawer-agree]');
    if (agree && !agree.checked) return;
    saveNote();
  }, true);

  document.addEventListener('click', function (event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var summary = event.target.closest && event.target.closest('.cart-drawer__note-summary, .cart-drawer-properties__summary, .cart-item-bundle__summary');
    if (summary) {
      var details = summary.parentNode;
      if (details && details.tagName === 'DETAILS' && drawer() && drawer().contains(details)) {
        event.preventDefault();
        event.stopPropagation();
        animateAccordion(details, !details.open);
        return;
      }
    }

    var closer = event.target.closest && event.target.closest('[data-cart-drawer-close], [data-cart-drawer-overlay]');
    if (closer && drawer() && drawer().contains(closer)) {
      event.preventDefault();
      requestClose();
      return;
    }

    var remove = event.target.closest && event.target.closest('[data-cart-drawer-remove]');
    if (remove && drawer() && drawer().contains(remove)) {
      event.preventDefault();
      var removeLine = remove.closest('[data-cart-line]');
      var removeInput = removeLine && removeLine.querySelector('[data-cart-drawer-qty]');
      if (removeInput) updateLine(removeInput, 0);
      return;
    }

    var delta = event.target.closest && event.target.closest('[data-cart-drawer-qty-delta]');
    if (delta && drawer() && drawer().contains(delta)) {
      event.preventDefault();
      var line = delta.closest('[data-cart-line]');
      var input = line && line.querySelector('[data-cart-drawer-qty]');
      if (!input) return;
      var current = parseInt(input.value, 10);
      if (isNaN(current) || current < 0) current = 0;
      var step = parseInt(delta.getAttribute('data-cart-drawer-qty-delta'), 10) || 0;
      updateLine(input, Math.max(0, current + step));
      return;
    }

    var guard = event.target.closest && event.target.closest('[data-cart-wallet-guard]');
    if (guard && drawer() && drawer().contains(guard)) {
      event.preventDefault();
      event.stopPropagation();
      termsBlocked(guard);
      return;
    }

    var wallet = event.target.closest && event.target.closest('.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout, shopify-paypal-button, shopify-google-pay-button, shopify-apple-pay-button');
    if (wallet && drawer() && drawer().contains(wallet)) {
      var agree = drawer().querySelector('[data-cart-drawer-agree]');
      if (agree && !agree.checked) {
        event.preventDefault();
        event.stopPropagation();
        termsBlocked(wallet);
        return;
      }
    }

    var checkout = event.target.closest && event.target.closest('[data-cart-drawer-checkout]');
    if (checkout) {
      event.preventDefault();
      event.stopPropagation();
      beginCheckout(checkout);
      return;
    }

    var link = event.target.closest && event.target.closest('a');
    if (isCartLink(link)) {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen()) requestClose();
      else requestOpen(link);
      return;
    }

    var marked = event.target.closest && event.target.closest('[data-cart-link]');
    if (marked && drawer() && !marked.closest('[data-cart-drawer]') && !marked.hasAttribute('data-cart-drawer-ignore')) {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen()) requestClose();
      else requestOpen(marked);
    }
  }, true);

  document.addEventListener('change', function (event) {
    if (event.target && event.target.closest && event.target.closest('[data-cart-drawer-agree]')) {
      syncWalletGuard();
      if (event.target.checked) saveNote();
    }
    var input = event.target && event.target.closest && event.target.closest('[data-cart-drawer-qty]');
    if (!input || !drawer() || !drawer().contains(input)) return;
    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = 0;
    updateLine(input, quantity);
  });

  document.addEventListener('keydown', function (event) {
    var root = drawer();
    if (!root || !root.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      requestClose();
      return;
    }

    var qty = event.target && event.target.closest && event.target.closest('[data-cart-drawer-qty]');
    if (qty && event.key === 'Enter') {
      event.preventDefault();
      qty.blur();
      return;
    }

    if (event.key !== 'Tab') return;
    var panel = root.querySelector('.cart-drawer__panel');
    var items = focusable(panel);
    if (!items.length) {
      event.preventDefault();
      if (panel) panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  });

  document.addEventListener('click', function (event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var recoNav = event.target.closest && (event.target.closest('[data-reco-prev]') || event.target.closest('[data-reco-next]'));
    if (recoNav) {
      event.preventDefault();
      var section = recoNav.closest('.cart-drawer__recommendations');
      var track = section && section.querySelector('[data-reco-track]');
      var step = recoStep(track);
      if (!track || !step) return;
      track.scrollBy({
        left: recoNav.hasAttribute('data-reco-prev') ? -step : step,
        behavior: reducedMotion() ? 'auto' : 'smooth'
      });
      return;
    }
    var recoAdd = event.target.closest && event.target.closest('[data-cart-drawer-reco-add]');
    if (recoAdd) {
      event.preventDefault();
      event.stopPropagation();
      addRecommendation(recoAdd);
      return;
    }
    if (!opensAfterAdd() || !window.ThemeCart) return;
    var match = productAddButton(event.target);
    if (!match) return;
    event.preventDefault();
    event.stopPropagation();
    addFromForm(match.form, match.button);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form && drawer() && drawer().contains(form) && form.classList.contains('cart-drawer__form')) {
      event.preventDefault();
      event.stopPropagation();
      var checkout = form.querySelector('[data-cart-drawer-checkout]');
      if (checkout) beginCheckout(checkout);
      return;
    }
    if (!opensAfterAdd() || !window.ThemeCart) return;
    if (!isAddForm(form)) return;
    var submitter = event.submitter;
    if (submitter && submitter.name !== 'add' && submitter.id !== 'purchase') return;
    event.preventDefault();
    event.stopPropagation();
    addFromForm(form, submitter || form.querySelector('[name="add"], #purchase'));
  }, true);

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target;
    if (!scope || !scope.querySelector || !scope.querySelector('[data-cart-drawer]')) return;
    unlockScroll();
    var root = drawer();
    if (root) root.classList.remove('is-open');
    setExpanded(false);
    syncWalletGuard();
    syncAccordions();
  });

  function storefrontRoot() {
    var routes = window.Shopify && window.Shopify.routes;
    var root = routes && typeof routes.root === 'string' ? routes.root : '/';
    if (!root || root === 'undefined' || root === 'null') root = '/';
    if (root.charAt(0) !== '/') root = '/' + root;
    return root;
  }

  function bareStorePath(pathname) {
    var path = String(pathname || '').replace(/\/+$/, '') || '/';
    var prefix = storefrontRoot().replace(/\/+$/, '') || '/';
    if (prefix !== '/' && (path === prefix || path.indexOf(prefix + '/') === 0)) {
      path = path.slice(prefix.length) || '/';
      if (path.charAt(0) !== '/') path = '/' + path;
    }
    return path;
  }

  function pathIsBlocked(pathname) {
    function blocked(path) {
      if (!path) return true;
      var markers = ['/checkout', '/checkouts', '/cart', '/orders', '/account/login', '/password', '/apps/bundles/cart'];
      for (var i = 0; i < markers.length; i++) {
        var marker = markers[i];
        if (path === marker || path.indexOf(marker + '/') === 0) return true;
        var at = path.indexOf(marker);
        if (at <= 0) continue;
        var end = at + marker.length;
        if (end !== path.length && path.charAt(end) !== '/') continue;
        if (/^\/[a-z]{2}(-[a-zA-Z]{2})?$/.test(path.slice(0, at))) return true;
      }
      return false;
    }
    var path = String(pathname || '').replace(/\/+$/, '') || '/';
    return blocked(bareStorePath(path)) || blocked(path);
  }

  function rememberPage() {
    try {
      if (pathIsBlocked(window.location.pathname)) return;
      window.sessionStorage.setItem('cart:last', window.location.href);
    } catch (error) {}
  }

  function isCartPage() {
    return pathIsBlocked(window.location.pathname) && /\/cart$/.test(String(window.location.pathname || '').replace(/\/+$/, '') || '/');
  }

  function openFromStorage() {
    if (isCartPage()) return;
    var flag = null;
    try {
      flag = window.sessionStorage.getItem('cart:open');
    } catch (error) {
      return;
    }
    if (flag !== '1') return;
    try {
      window.sessionStorage.removeItem('cart:open');
    } catch (error) {}
    if (drawer()) requestOpen(null);
  }

  rememberPage();
  openFromStorage();
  window.addEventListener('pageshow', function () {
    rememberPage();
    openFromStorage();
  });
  syncWalletGuard();
  syncAccordions();

  if (drawer() && window.MutationObserver && !drawer().hasAttribute('data-cart-wallet-watch')) {
    drawer().setAttribute('data-cart-wallet-watch', '');
    var walletObserver = new MutationObserver(function () {
      syncWalletGuard();
      syncAccordions();
    });
    walletObserver.observe(drawer(), { childList: true, subtree: true });
  }
})();
